import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../components/ui";
import { thumbOf } from "../../lib";
import type { VehicleOption } from "../../types";

// ---------------------------------------------------------------------------
// Fotos: miniatura de unidad (con respaldo por tipo), foto genérica con
// respaldo y galería con visor a pantalla completa.
// ---------------------------------------------------------------------------

const TYPE_ICON: Record<string, string> = {
  truck: "ri-truck-line",
  trailer: "ri-truck-line",
  pickup: "ri-roadster-line",
  van: "ri-bus-2-line",
  car: "ri-car-line",
  suv: "ri-car-line",
};

const SIZE = {
  xs: "h-8 w-10",
  sm: "h-10 w-14",
  md: "h-14 w-20",
  lg: "h-20 w-28",
  xl: "h-28 w-40",
} as const;

export interface PhotoProps {
  src?: string | null;
  alt: string;
  /** Usa la miniatura `-thumb` si es foto del Sandbox. */
  thumb?: boolean;
  fallbackIcon?: string;
  className?: string;
  imgClassName?: string;
}

/** Imagen con carga diferida y respaldo (icono) si falta o falla. */
export function Photo({ src, alt, thumb = false, fallbackIcon = "ri-image-line", className, imgClassName }: PhotoProps) {
  const [failed, setFailed] = useState(false);
  const url = src ? (thumb ? thumbOf(src) : src) : null;
  useEffect(() => setFailed(false), [url]);
  return (
    <div className={cn("relative overflow-hidden bg-subtle", className)}>
      {url && !failed ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className={cn("h-full w-full object-cover", imgClassName)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-content-faint">
          <i className={cn(fallbackIcon, "text-xl")} />
        </div>
      )}
    </div>
  );
}

export interface VehicleThumbProps {
  vehicle?: VehicleOption | null;
  size?: keyof typeof SIZE;
  className?: string;
  /** Punto de estado sobre la foto (p. ej. en taller). */
  badge?: "shop" | "ok" | "alert" | null;
}

export function VehicleThumb({ vehicle, size = "sm", className, badge }: VehicleThumbProps) {
  const icon = TYPE_ICON[vehicle?.type || ""] || "ri-truck-line";
  return (
    <div className={cn("relative shrink-0", SIZE[size], className)}>
      <Photo
        src={vehicle?.image}
        alt={vehicle?.label || ""}
        thumb={size !== "xl"}
        fallbackIcon={icon}
        className="h-full w-full rounded-lg ring-1 ring-line"
      />
      {badge ? (
        <span
          className={cn(
            "absolute -right-1 -top-1 h-3 w-3 rounded-full ring-2 ring-card",
            badge === "shop" ? "bg-warning" : badge === "alert" ? "bg-error" : "bg-success"
          )}
        />
      ) : null}
    </div>
  );
}

export interface PhotoGalleryProps {
  photos: string[];
  /** Texto alternativo base («Evidencia 1», …). */
  alt: string;
  className?: string;
  /** Miniaturas en fila horizontal (tarjeta) o rejilla (detalle). */
  layout?: "grid" | "strip";
  max?: number;
}

/** Rejilla de miniaturas; al hacer clic abre el visor. */
export function PhotoGallery({ photos, alt, className, layout = "grid", max }: PhotoGalleryProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState<number | null>(null);
  const shown = max ? photos.slice(0, max) : photos;
  const extra = max ? photos.length - shown.length : 0;
  if (!photos.length) return null;
  return (
    <>
      <div className={cn(layout === "grid" ? "grid grid-cols-3 gap-2" : "flex gap-1.5", className)}>
        {shown.map((p, i) => (
          <button
            key={`${p}-${i}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(i);
            }}
            aria-label={t("mantenimiento.ui.gallery.open")}
            className={cn(
              "group relative overflow-hidden rounded-lg ring-1 ring-line focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
              layout === "grid" ? "aspect-[4/3]" : "h-10 w-14 shrink-0"
            )}
          >
            <Photo src={p} alt={`${alt} ${i + 1}`} thumb className="h-full w-full" imgClassName="transition-transform duration-300 group-hover:scale-105" />
            {extra > 0 && i === shown.length - 1 ? (
              <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-xs font-semibold text-white">+{extra}</span>
            ) : null}
          </button>
        ))}
      </div>
      {open !== null ? <Lightbox photos={photos} index={open} alt={alt} onClose={() => setOpen(null)} onIndex={setOpen} /> : null}
    </>
  );
}

interface LightboxProps {
  photos: string[];
  index: number;
  alt: string;
  onClose: () => void;
  onIndex: (i: number) => void;
}

export function Lightbox({ photos, index, alt, onClose, onIndex }: LightboxProps) {
  const { t } = useTranslation("common");
  const go = useCallback((d: number) => onIndex((index + d + photos.length) % photos.length), [index, photos.length, onIndex]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      <img
        src={photos[index]}
        alt={`${alt} ${index + 1}`}
        className="max-h-[85vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <button type="button" onClick={onClose} aria-label={t("mantenimiento.ui.gallery.close")} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20">
        <i className="ri-close-line text-xl" />
      </button>
      {photos.length > 1 ? (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label={t("mantenimiento.ui.gallery.prev")} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20">
            <i className="ri-arrow-left-s-line text-2xl" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label={t("mantenimiento.ui.gallery.next")} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20">
            <i className="ri-arrow-right-s-line text-2xl" />
          </button>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white">
            {t("mantenimiento.ui.gallery.counter", { n: index + 1, total: photos.length })}
          </span>
        </>
      ) : null}
    </div>
  );
}
